/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatCal = require('../types/NatCal')
const kernel = require('../kernel/kernel')
const CCalculAncetre = require('./CCalculAncetre')
const CValeur = require('./CValeur')
module.exports = CSuiteRec

/**
 * Classe représentant une suite récurrente de la forme u(n+1)=f[u(n)]
 * Pour utiliser un terme de la suite dans un calcul, utiliser une syntae de la forme
 * u(n) où u est le nom de la suite.
 * @constructor
 * @extends CCalculAncetre
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {Boolean} estElementFinal : true si élément final de construction.
 * @param {String} nomCalcul : Le nom de la suite.
 * @param {CFonc} fonctionAssociee : La fonction réelle d'une variable associée (fonction utilisateur).
 * @param {CValeur} nombreTermes : Le nombre de termes de la suite (dynamique).
 * @param {CValeur} premierTerme : Le premier terme de la suite.
 * @returns {CSuiteRec}
 */
function CSuiteRec (listeProprietaire, impProto, estElementFinal, nomCalcul, fonctionAssociee,
    nombreTermes, premierTerme) {
  if (arguments.length === 0) return;
  if (arguments.length === 1) CCalculAncetre.call(this, listeProprietaire);
  else {
    CCalculAncetre.call(this, listeProprietaire, impProto, estElementFinal, nomCalcul);
    this.fonctionAssociee = fonctionAssociee;
    this.nombreTermes = nombreTermes;
    this.premierTerme = premierTerme;
    this.metAJourNombreTermes();
  }
}
CSuiteRec.prototype = new CCalculAncetre();
CSuiteRec.prototype.constructor = CSuiteRec;
CSuiteRec.prototype.superClass = "CCalculAncetre";
CSuiteRec.prototype.className = "CSuiteRec";

CSuiteRec.prototype.metAJourNombreTermes = function() {
  var val = this.nombreTermes.rendValeur();
  if ((val === Math.floor(val)) && (val >= 1) && (val <= kernel.NombreMaxiTermesSuiteRec)) this.nbt = val;
  else this.nbt = 2; // Pas d'importance car dans ce cas la suite n'existe pas
  this.valeurs = new Array(this.nbt);
};
CSuiteRec.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.fonctionAssociee);
  var ind2 = listeSource.indexOf(this.impProto);
  var premierTermeClone = this.premierTerme.getClone(listeSource, listeCible);
  var nombreTermesClone = this.nombreTermes.getClone(listeSource, listeCible);
  return new CSuiteRec(listeCible, listeCible.get(ind2, "CImplementationProto"),
    this.estElementFinal, this.nomCalcul, listeCible.get(ind1, "CSuiteRec"), nombreTermesClone,
    premierTermeClone);
};
CSuiteRec.prototype.setClone = function(ptel) {
  CCalculAncetre.prototype.setClone.call(this, ptel);
  this.indiceDernierTermeExistant = ptel.indiceDernierTermeExistant;
  var nbt = ptel.valeurs.length;
  if (this.valeurs.length !== nbt) this.valeurs = new Array(nbt);
  for (var i = 0; i <= this.indiceDernierTermeExistant; i++) {
    this.valeurs[i] = ptel.valeurs[i];
  }
};
CSuiteRec.prototype.nombreVariables = function() {
  return 1;
};
CSuiteRec.prototype.initialisePourDependance = function() {
  CCalculAncetre.prototype.initialisePourDependance.call(this);
  this.nombreTermes.initialisePourDependance();
  this.premierTerme.initialisePourDependance();
};
CSuiteRec.prototype.dependDe = function(p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CCalculAncetre.prototype.dependDe.call(this, p)
    || this.premierTerme.dependDe(p) || this.fonctionAssociee.dependDe(p) || this.nombreTermes.dependDe(p));
};
CSuiteRec.prototype.dependDePourBoucle = function(p) {
  return (p === this) || this.premierTerme.dependDePourBoucle(p) || this.fonctionAssociee.dependDePourBoucle(p)
  || this.nombreTermes.dependDePourBoucle(p);
};
CSuiteRec.prototype.positionne = function(infoRandom, dimfen) {
  var u,v;
  this.nombreTermes.positionne(infoRandom, dimfen);
  this.premierTerme.positionne(infoRandom, dimfen);
  this.existe = this.fonctionAssociee.existe && this.premierTerme.existe && this.nombreTermes.existe;
  if (!this.existe) return;
  var dnbt = Math.floor(this.nombreTermes.rendValeur() + 0.5);
  this.existe = (dnbt >= 1) && (dnbt <= kernel.NombreMaxiTermesSuiteRec);
  if (!this.existe) return;
  if (dnbt !== this.nbt) {
    this.nbt = dnbt;
    this.metAJourNombreTermes();
  }
  u = this.premierTerme.rendValeur();
  this.valeurs[0] = u;
  this.indiceDernierTermeExistant = 0;
  var i = 1;
  while (i < this.nbt) {
    try {
      this.listeProprietaire.initialiseNombreIterations();
      // v = fonctionAssociee.calcul.resultatFonction(infoRandom, u);
      v = this.fonctionAssociee.calcul.resultatFonction(true, u);
      if (!isFinite(v)) break;
      u = v;
      this.valeurs[i] = u;
      i++;
    } catch (e) {
      break;
    }
  }
  i--;
  this.indiceDernierTermeExistant = i;
};
CSuiteRec.prototype.confonduAvec = function(p) {
  if (p.className === this.className) {
    return (this.fonctionAssociee === p.fonctionAssociee) && (this.premierTerme.confonduAvec(p.premierTerme)
        && (this.nombreTermes.confonduAvec(p.nombreTermes)));
  }
  else return false;
};
CSuiteRec.prototype.rendValeurFonction = function(infoRandom, parametre){
  if (!this.existe || (parametre < 0) || (parametre !== Math.floor(parametre))
  || (parametre > this.indiceDernierTermeExistant)) {
    throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  } else return this.valeurs[parametre];
};
CSuiteRec.prototype.getNatureCalcul = function() {
  return NatCal.NSuiteRecurrenteReelle;
};
CSuiteRec.prototype.estFonctionOuSuite = function() {
  return true;
};
CSuiteRec.prototype.read = function(inps, list){
 CCalculAncetre.prototype.read.call(this, inps, list);
 this.nombreTermes = new CValeur();
 this.nombreTermes.read(inps, list);
 this.premierTerme = new CValeur();
 this.premierTerme.read(inps, list);
 var ind1 = inps.readInt();
 this.fonctionAssociee = list.get(ind1,"CSuiteRec");
 // metAJourNombreTermes();
 // Le nombre de valeurs étant dynamique, on ne peut pas le connaître au chargement
 // On met 1 terme arbitrairement et cela sera rectifié dans positionne();
 this.nbt = 1;
 this.valeurs = new Array(1);
};
CSuiteRec.prototype.write = function(oups, list){
  CCalculAncetre.prototype.write.call(this, oups, list);
  this.nombreTermes.write(oups, list);
  this.premierTerme.write(oups, list);
  var ind1 = list.indexOf(this.fonctionAssociee);
  oups.writeInt(ind1);
};



// WEBPACK FOOTER //
// ./src/objets/CSuiteRec.js